import { createSlice } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';

const initialState = {
  dob: null,
  firstName: "",
  lastName: "",
  parent: {
    name: "",
    relationship: "",
    soloTreatment: "",
  },
  weight: "",
  address: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
  },
  payMethod: "",
  concern: "",
  tried: [],
  prescriptions: [],
  duration: "",
  sex: {
    sexAtBirth: "",
    maternity: [],
    cycle: "",
    additional: [],
    birthControlMethods: [],
    lmpDate: "",
    otherBirthControl: "",
    contraceptive: [],
    otherContraceptive: ""
  },
  allergies: {
    has: "",
    description: "",
  },
  paid: false,
  photos: [],
  bodyPart: [],
  otherMeds: {
    has: "",
    meds: [],
  },
  other: "",
  hearAbt: "",
  phoneNo: "",
  email: "",
  tellescopeID: "",
  booked: false,
  returningCondition: "",
  pickedMedication: "",
  onPrescribedMedication: "",
  howWellWorks: "",
  sideEffects: {
    has: "",
    description: "",
  },
  whyStoppedMedication: [],
  followUpCare: [],
  questionsForDoctor: "",
  newPhotos: [],
  returningFullName: "",
  returning: false,
  inStateForCare: false,
  caseID: null,
  uberDoc: false,
  zocDoc: false,
  firefly: false,
  patientHistory: {
    liverDisease: '',
    diabetes: '',
    elevatedTriglycerides: '',
    pancreatitis: '',
    inflammatoryBowelDisease: '',
    smoking: '',
    alcohol: '',
    illicitDrugs: '',
    priorIsotretinoinUse: '',
    contactLens: '',
    historyOfDepression: '',
    suicidalThoughts: '',
    suicideAttempt: '',
  },
  treatmentContinuation: {
    continueTreatment: '',
    interestedInAccutane: '',
  },
  adultWeight: '',
  selfPayIntake: false,
  missingPatient: false,
  skippedInsurance: false,
  patientID: '',
  previousCaseID: '',
  retIntakeID: '',
  patients: [],
  imagesUploaded: false,
  currentPage: '',
  selectedSlot: null,
  skippedPhotos: 'no',
  photosUpload: false,
  portal: false,
  intakeID: null,
  abandonedIntake: false,
  seedDob: null,
  seedFirstName: "",
  seedLastName: "",
  readyToPersist: false,
  smsConsent: false,
  missedDoses: {
    has: "",
    pillsLeft: 0
  },
  accutaneSideEffects: {
    selected: [],
    otherText: ""
  },
  isIsotretinoin: false,
  medicalHistoryChanges: {
    has: "",
    description: "",
  },
  newMedicationsSinceLastVisit: {
    has: "",
    description: "",
  },
  phq9: {
    q1_little_interest: '',
    q2_feeling_down: '',
    q3_sleep: '',
    q4_tired: '',
    q5_appetite: '',
    q6_feeling_bad_about_self: '',
    q7_concentration: '',
    q8_psychomotor: '',
    q9_self_harm: '',
  },
  mentalHealthTrigger: false,
  selectedReturningPatient: null,
  selectedPhotosPatient: null,
  photosCaseOptions: [],
  selectedAppointmentPatient: null,
  appointmentToReschedule: null,
};

const MENTAL_HEALTH_SIDE_EFFECTS = new Set([
  "Mood changes",
  "Depression",
  "Loss of interest in normal activities",
  "Thoughts of hurting yourself",
]);


const userSlice = createSlice({
  name: 'user',
  initialState:
    initialState,
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action) => {
      if (action.payload && action.payload.user) {
        return {
          ...state,
          ...action.payload.user
        };
      }
      return state;
    });
  },
  reducers: {
    reset: (state, action) => {
      return { ...initialState };
    },
    setFullName: (state, action) => {
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
    },
    setDob: (state, action) => {
      state.dob = action.payload;
    },
    setParent: (state, action) => {
      state.parent = action.payload;
    },
    setWeight: (state, action) => {
      state.weight = action.payload;
    },
    setAddress: (state, action) => {
      state.address = action.payload;
    },
    setPayMethod: (state, action) => {
      state.payMethod = action.payload;
    },
    setConcern: (state, action) => {
      const newConcern = action.payload;
      // If concern is changing, clear prescriptions since they are concern-specific
      if (state.concern !== newConcern) {
        state.prescriptions = [];
      }
      state.concern = newConcern;
    },
    setTried: (state, action) => {
      state.tried = Array.isArray(action.payload) ? action.payload : [];
    },
    setPrescriptions: (state, action) => {
      state.prescriptions = action.payload;
    },
    setDuration: (state, action) => {
      state.duration = action.payload;
    },
    setSex: (state, action) => {
      state.sex = {
        ...state.sex,
        ...action.payload,
        maternity: Array.isArray(action.payload.maternity) ? action.payload.maternity : [],
        cycle: action.payload.cycle ?? state.sex.cycle,
        additional: action.payload.additional ?? state.sex.additional,
        birthControlMethods: Array.isArray(action.payload.birthControlMethods) ? action.payload.birthControlMethods : [],
        lmpDate: action.payload.lmpDate ?? state.sex.lmpDate,
        otherBirthControl: action.payload.otherBirthControl ?? state.sex.otherBirthControl,
        contraceptive: Array.isArray(action.payload.contraceptive) ? action.payload.contraceptive : [],
        otherContraceptive: action.payload.otherContraceptive ?? state.sex.otherContraceptive,
      };
    },
    setAllergies: (state, action) => {
      state.allergies = action.payload;
    },
    setPaid: (state, action) => {
      state.paid = action.payload;
    },
    setPhotos: (state, action) => {
      state.photos = action.payload;
    },
    setBodyPart: (state, action) => {
      state.bodyPart = action.payload;
    },
    setOtherMeds: (state, action) => {
      state.otherMeds = action.payload;
    },
    setOther: (state, action) => {
      state.other = action.payload;
    },
    setHearAbt: (state, action) => {
      state.hearAbt = action.payload;
    },
    setSummary: (state, action) => {
      state.summary = action.payload;
    },
    setPhoneNo: (state, action) => {
      state.phoneNo = action.payload;
    },
    setEmail: (state, action) => {
      state.email = action.payload;
    },
    setOtpCode: (state, action) => {
      state.otpCode = action.payload;
    },
    setTellescopeID: (state, action) => {
      state.tellescopeID = action.payload;
    },
    setBooked: (state, action) => {
      state.booked = action.payload;
    },
    setReturningCondition: (state, action) => {
      state.returningCondition = action.payload;
    },
    setPickedMedication: (state, action) => {
      state.pickedMedication = action.payload;
    },
    setOnPrescribedMedication: (state, action) => {
      state.onPrescribedMedication = action.payload;
    },
    setHowWellWorks: (state, action) => {
      state.howWellWorks = action.payload;
    },
    setSideEffects: (state, action) => {
      state.sideEffects = action.payload;
    },
    setWhyStoppedMedication: (state, action) => {
      state.whyStoppedMedication = Array.isArray(action.payload) ? action.payload : [];
    },
    setFollowUpCare: (state, action) => {
      state.followUpCare = Array.isArray(action.payload) ? action.payload : [];
    },
    setQuestionsForDoctor: (state, action) => {
      state.questionsForDoctor = action.payload;
    },
    setNewPhotos: (state, action) => {
      state.newPhotos = action.payload;
    },
    setReturningFullName: (state, action) => {
      state.returningFullName = action.payload;
    },
    setAllPatientData: (state, action) => {
      return { ...state, ...action.payload };
    },
    setReturning: (state, action) => {
      state.returning = action.payload;
    },
    setInStateForCare: (state, action) => {
      state.inStateForCare = action.payload;
    },
    setCaseId: (state, action) => {
      state.caseID = action.payload;
    },
    setUberDoc: (state, action) => {
      state.uberDoc = action.payload;
    },
    setZocDoc: (state, action) => {
      state.zocDoc = action.payload;
    },
    setFirefly: (state, action) => {
      state.firefly = action.payload;
    },
    setPatientHistory: (state, action) => {
      state.patientHistory = action.payload;
    },
    setTreatmentContinuation: (state, action) => {
      state.treatmentContinuation = action.payload;
    },
    setAdultWeight: (state, action) => {
      state.adultWeight = action.payload;
    },
    setSelfPayIntake: (state, action) => {
      state.selfPayIntake = action.payload;
    },
    setMissingPatient: (state, action) => {
      state.missingPatient = action.payload;
    },
    setSkippedInsurance: (state, action) => {
      state.skippedInsurance = action.payload;
    },
    setPreviousCaseId: (state, action) => {
      state.previousCaseID = action.payload;
    },
    setRetIntakeId: (state, action) => {
      state.retIntakeID = action.payload;
    },
    setPatientID: (state, action) => {
      state.patientID = action.payload;
    },
    setPatients: (state, action) => {
      state.patients = action.payload;
    },
    markUserUpdateReady: (state) => {
      // This action just triggers the middleware for the first partial progress call
      state.readyToPersist = true;
    },
    setImagesUploaded: (state, action) => {
      state.imagesUploaded = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setSelectedSlot: (state, action) => {
      state.selectedSlot = action.payload;
    },
    setSkippedPhotos: (state, action) => {
      state.skippedPhotos = action.payload;
    },
    setPhotosUpload: (state, action) => {
      state.photosUpload = action.payload;
    },
    setPortal: (state, action) => {
      state.portal = action.payload;
    },
    setUserFromAbandonedCart: (state, action) => {
      const p = action.payload || {};
      const caseObj = p.case || {};
      const intake = p.intake || p.case?.intakes?.[0] || {};
      const patient = p.patient || {};
      const contact = patient.contact || {};
      const fields = patient.fields || {};
      const mc = intake.menstrual_cycle || {};
      state.abandonedIntake = true;
      state.readyToPersist = false;

      // essential IDs
      if (caseObj.id) state.caseID = caseObj.id;
      if (patient.id) state.patientID = patient.id;
      state.currentPage = intake.current_page || p.current_page || state.currentPage || '';
      state.intakeID = intake.id ?? state.intakeID;
      state.tellescopeID = patient.tellescope_patient_id || state.tellescopeID;
      if (caseObj.is_paid) state.paid = caseObj.is_paid;

      // patient basics
      state.dob = patient.date_of_birth || state.dob;
      state.firstName = patient.first_name || state.firstName;
      state.lastName = patient.last_name || state.lastName;
      state.email = contact.email || state.email;
      state.phoneNo = contact.phone || state.phoneNo;

      // abandoned seed 
      state.seedDob = patient.date_of_birth || state.seedDob;
      state.seedFirstName = patient.first_name || state.seedFirstName;
      state.seedLastName = patient.last_name || state.seedLastName;

      state.address = {
        line1: patient.address_line_1 || state.address.line1 || '',
        line2: patient.address_line_2 || state.address.line2 || '',
        city: patient.city || state.address.city || '',
        state: patient.state || state.address.state || '',
        zip: patient.zipcode || state.address.zip || '',
      };

      // guardian details
      state.parent = {
        name: patient.guardian_name || '',
        relationship: '', // TBD - not stored or returned from backend 
        soloTreatment: '' // TBD - not stored or returned from backend 
      };

      // sex & reproductive details
      state.sex = {
        ...state.sex,
        sexAtBirth: patient.sex || state.sex.sexAtBirth,
        cycle: mc.cycle ?? state.sex.cycle,
        additional: Array.isArray(mc.additional_cycle_details) ? mc.additional_cycle_details : state.sex.additional,
        birthControlMethods: Array.isArray(mc.contraceptive) ? mc.contraceptive : state.sex.birthControlMethods,
        contraceptive: Array.isArray(mc.contraceptive) ? mc.contraceptive : state.sex.contraceptive,
        otherBirthControl: mc.other_contraceptive ?? state.sex.otherBirthControl, // accutane field
        otherContraceptive: mc.other_contraceptive ?? state.sex.otherContraceptive,
        maternity: Array.isArray(patient.pregnancy_status) ? patient.pregnancy_status : state.sex.maternity,
        lmpDate: '', // accutane feature related field 
      };

      // intake details
      state.inStateForCare = Boolean(intake.in_state_for_care);
      state.concern = intake.patient_reported_concern || state.concern;
      state.duration = intake.duration || state.duration;
      state.other = intake.current_symptoms || state.other;
      state.weight = intake.weight || state.weight;


      // tried before
      // backend data is as follow: "{\"Tried prescription(s)\",\"Tried something else (over-the-counter drug, diet changes, other)\"}"
      // converting that to array of strings 
      const historyStr = typeof intake.history === 'string' ? intake.history : '';
      let tried = [];
      if (Array.isArray(intake.history)) {
        tried = intake.history;
      } else if (historyStr) {
        const cleaned = historyStr.replace(/^[[{]|[}\]]$/g, '');
        tried = cleaned
          .split(/","|",\s*"/)
          .map(s => s.replace(/^"|"$/g, '').trim())
          .filter(Boolean);
      }
      if (tried.length) state.tried = tried;

      // body parts
      if (Array.isArray(intake.body_parts)) state.bodyPart = intake.body_parts;

      // allergies
      const allergyText = patient.allergies ?? intake.allergies ?? '';
      state.allergies = {
        has: allergyText ? 'y' : 'n',
        description: allergyText || '',
      };

      // photos
      const photoUrls = Array.isArray(intake.patient_photos)
        ? intake.patient_photos.map(ph => ph.signed_url).filter(Boolean)
        : [];
      if (photoUrls.length) {
        state.photos = photoUrls;
        state.imagesUploaded = true;
      }

      // prescriptions (related)
      if (Array.isArray(intake.patient_reported_related_medications)) {
        state.prescriptions = intake.patient_reported_related_medications.map(m => ({
          name: m.name || '',
          duration: m.duration || '',
          help: m.help || '',
        }));
      }

      // otherMeds (non-related)
      if (Array.isArray(intake.patient_reported_medications)) {
        state.otherMeds = {
          has: intake.patient_reported_medications.length ? 'y' : '',
          meds: intake.patient_reported_medications.slice(),
        };
      }

      // payment method
      if (typeof fields.Payer === 'string' && fields.Payer) {
        const lower = fields.Payer.toLowerCase();
        state.payMethod = lower.includes('insurance')
          ? 'insurance'
          : lower.includes("paying for myself") || lower.includes('self')
            ? 'self'
            : fields.Payer;
      }

      state.skippedInsurance = Boolean(fields.skipped_insurance);
      if (typeof fields.skipped_photos === 'string') state.skippedPhotos = fields.skipped_photos;
      if (intake.reference) state.hearAbt = intake.reference;

      // previous case link 
      if (caseObj.previous_case_id) state.previousCaseID = caseObj.previous_case_id;
      if (fields.zocDoc) state.zocDoc = Boolean(fields.zocDoc);
    },
    setPruneAbandonedCart: (state, action) => {
      const {
        firstName,
        lastName,
        dob,
        email,
        phoneNo,
        address = {},
        concern = "",
      } = action.payload || {};

      const fresh = { ...initialState };

      fresh.firstName = firstName ?? "";
      fresh.lastName = lastName ?? "";
      fresh.dob = dob ?? null;
      fresh.email = email ?? "";
      fresh.phoneNo = phoneNo ?? "";
      fresh.address = {
        ...initialState.address,
        ...address,
      };
      fresh.concern = concern ?? "";
      fresh.currentPage = '/demographic';

      fresh.caseID = null;
      fresh.patientID = '';
      fresh.intakeID = null;
      fresh.previousCaseID = '';
      fresh.tellescopeID = '';

      fresh.seedFirstName = fresh.firstName || "";
      fresh.seedLastName = fresh.lastName || "";
      fresh.seedDob = fresh.dob || null;

      fresh.abandonedIntake = false;
      fresh.readyToPersist = false;

      return fresh;

    },
    setSmsConsent: (state, action) => {
      state.smsConsent = action.payload;
    },
    setMissedDoses: (state, action) => {
      state.missedDoses = action.payload;
    },
    setAccutaneSideEffects: (state, action) => {
      state.accutaneSideEffects = action.payload;

      const selected = Array.isArray(action.payload?.selected)
        ? action.payload.selected
        : [];

      state.mentalHealthTrigger = selected.some((s) =>
        MENTAL_HEALTH_SIDE_EFFECTS.has(s)
      );
    },
    setMedicalHistoryChanges: (state, action) => {
      state.medicalHistoryChanges = action.payload;
    },
    setNewMedicationsSinceLastVisit: (state, action) => {
      state.newMedicationsSinceLastVisit = action.payload; 
    },
    setPhq9Answer: (state, action) => {
      const { key, value } = action.payload;
      state.phq9 = {
        ...state.phq9,
        [key]: value,
      };
    },
    setMentalHealthTrigger: (state, action) => {
      state.mentalHealthTrigger = Boolean(action.payload);
    },
    setSelectedReturningPatient: (state, action) => {
      state.selectedReturningPatient = action.payload;
    },
    setSelectedPhotosPatient: (state, action) => {
      state.selectedPhotosPatient = action.payload;
    },
    setPhotosCaseOptions: (state, action) => {
      state.photosCaseOptions = Array.isArray(action.payload) ? action.payload : [];
    },
    setSelectedAppointmentPatient: (state, action) => {
      state.selectedAppointmentPatient = action.payload;
    },
    setAppointmentToReschedule: (state, action) => {
      state.appointmentToReschedule = action.payload;
    },
    clearAppointmentToReschedule: (state) => {
      state.appointmentToReschedule = null;
    },
  }
});

export const { reset, setFullName, setDob, setParent, setWeight, setAddress, setPayMethod, setConcern,
  setTried, setPrescriptions, setDuration, setSex, setAllergies, setPaid, setPhotos, setBodyPart, setOtherMeds, setOther,
  setHearAbt, setSummary, setOtpCode, setEmail, setPhoneNo, setTellescopeID, setBooked, setReturningCondition, setPickedMedication,
  setOnPrescribedMedication, setHowWellWorks, setSideEffects, setWhyStoppedMedication, setFollowUpCare, setQuestionsForDoctor, setNewPhotos,
  setReturningFullName, setAllPatientData, setReturning, setInStateForCare, setCaseId, setUberDoc, setZocDoc, setFirefly, setPatientHistory,
  setTreatmentContinuation, setAdultWeight, setSelfPayIntake, setMissingPatient, setSkippedInsurance, setPreviousCaseId,
  setRetIntakeId, setPatientID, setPatients, markUserUpdateReady, setImagesUploaded, setCurrentPage, setSelectedSlot, setSkippedPhotos,
  setPhotosUpload, setPortal, setUserFromAbandonedCart, setPruneAbandonedCart, setSmsConsent, setMissedDoses, setAccutaneSideEffects, setMedicalHistoryChanges, 
  setNewMedicationsSinceLastVisit, setPhq9Answer, setMentalHealthTrigger, setSelectedReturningPatient, setSelectedPhotosPatient, setPhotosCaseOptions, 
  setSelectedAppointmentPatient, setAppointmentToReschedule, clearAppointmentToReschedule}
  = userSlice.actions;

export default userSlice.reducer;
